import { Component, OnInit, Input, SimpleChanges, ViewChild, Renderer2, AfterViewInit, Output, EventEmitter, ElementRef } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { TimeSeriesDetail } from 'src/app/models/dashboard';
import { ElapsedTimeService } from '../../../service/elapsed-time.service';
import { SiteTwin, Zone } from 'src/api/models';
import { SmartImageComponent } from '../smart-image/smart-image.component';
import { AzureAuthService } from 'src/service/azure-auth.service';

interface PreviousLightStackElement {
  key: string;
  element: Element;
}

export class SmartImageDetail {
  src: string;
  overlay: string;
  timeStamp: string;
}

@Component({
  selector: 'smart-image-list',
  templateUrl: './smart-image-list.component.html',
  styleUrls: ['./smart-image-list.component.css']
})
export class SmartImageListComponent implements OnInit, AfterViewInit {
  @ViewChild('smartImage') smartImage: SmartImageComponent;
  
  @Input() showZones: boolean;
  @Input() zones: Zone[];
  
  @Input() showLineAt: any;

  public _images: SmartImageDetail[];
  @Input() set images(images: SmartImageDetail[]){
    this._images = images;
    this.preloadImages(images);
    // show the latest image
    if (this.hasImages){
      this.selectImage(this._images.length - 1);
      console.log("Images Loaded: " + this._images.length);
    }else{
      this.selectImage(null);
    }
  };

  selectedImage: SmartImageDetail;
  @Input() showMetaData: boolean;
  @Input() onImageIndexChange: EventEmitter<number> = new EventEmitter<number>();
  
  @Output() objectSelected = new EventEmitter<any>();
  @Output() pointerEvent = new EventEmitter<any>();
  @Output() clickEvent = new EventEmitter<any>();

  @Input() overlayConfig: any;
  @Input() public aspectRatio: string = '16:9';
  @Input() public isLoaded: boolean = false;

  public showImg: boolean = true;
  error: boolean = false;

  imageRef:any;
  imageRefTarget:any;

  overlays: any[];

  public isPlay: boolean = false;
  public repeat: boolean = false;
  public isGeneratingVideo: boolean = false;
  public videoGenerationProgress: number = 0;
  public videoGenerationStatus: string = '';
  private videoGenerationCancelled: boolean = false;
  private preloadRunId: number = 0;
  private preloadedImages: HTMLImageElement[] = [];

  public selectedImageIndex: number = 0;

  get hasNoImages(): boolean {
    return !this.hasImages;
  }

  private get hasImages(): boolean {
    return !!this._images && this._images.length > 0 && this._images.some(image => !!image?.src);
  }

  constructor(private renderer:Renderer2, private elapsedTimeService:ElapsedTimeService, public security: AzureAuthService) {
    
  }



  ngOnChanges(changes: SimpleChanges): void {
    this.smartImage?.showImage();
  }

  ngOnInit(): void {
    if (this._images && this._images.length > 0){
      this.selectedImage = this._images[0];
    }
    if (this.aspectRatio == null){
      this.aspectRatio = '16:9';
    }
    this.isLoaded = true;
  }

  ngAfterViewInit() {
    this.smartImage?.showImage();
  }

  pointerEventEnabled: boolean = true;
  togglePointerEvent(){
    this.pointerEventEnabled = !this.pointerEventEnabled;
  }

  emitPointerEvent(event){
    if (this.pointerEventEnabled){
      this.pointerEvent.emit(event);
    }
  }

  emitClickEvent(event){
    this.clickEvent.emit(event);
  }

  timer: any;
  play(){
    console.log('play');
    this.isPlay = true;
    this.selectedImageIndex = 0;
    this.timer = setInterval(() => this.nextImage(), 100);
  }

  stop(){
    this.isPlay = false;
    clearInterval(this.timer);
  }
  
  nextImage(){
    if (this._images){
      var index = Math.min(this._images.length, this.selectedImageIndex + 1);
      if (index == this._images.length){
        if (this.repeat){
          index = 0;
        }
        else{
          index = this._images.length - 1;
          this.stop();
        }
      }
      this.selectedImageIndex = index;
      //this.changeImage(0);
      this.changeImage();
    }
  }

  prevImage(){
    if (this._images){
      var index = this.selectedImageIndex - 1;
      if (index < 0){
        index = this._images.length - 1;
      }
      this.selectedImageIndex = index;
      this.changeImage();
    }
  }

  selectImage(index) {
    this.selectedImageIndex = index;
    this.changeImage();
  }
  
  changeImage(){
    if (this._images){
      this.selectedImage = this._images[this.selectedImageIndex];
      console.log('[SmartImageList] Selected image overlay:', this.selectedImage?.overlay);
      this.smartImage?.showImage();
      this.isLoaded = true;
      if (this.onImageIndexChange){
        this.onImageIndexChange.emit(this.selectedImageIndex);
      }
    }
  }

  private preloadImages(images: SmartImageDetail[]): void {
    const runId = ++this.preloadRunId;
    this.preloadedImages = [];

    if (!images || images.length === 0) {
      return;
    }

    console.log(`[SmartImageList] Preloading ${images.length} image(s)`);
    images.forEach((detail, index) => {
      if (!detail?.src) {
        return;
      }

      const image = new Image();
      image.onload = () => {
        if (runId !== this.preloadRunId) {
          return;
        }
        this.preloadedImages.push(image);
      };
      image.onerror = () => {
        if (runId === this.preloadRunId) {
          console.warn(`[SmartImageList] Failed to preload image ${index + 1}/${images.length}: ${detail.src}`);
        }
      };
      image.src = detail.src;
    });
  }

  cancelVideoGeneration() {
    this.videoGenerationCancelled = true;
    this.videoGenerationStatus = 'Cancelling...';
    console.log('Cancel requested');
  }

  async downloadVideo() {
    if (!this._images || this._images.length === 0) {
      console.error('No images available to create video');
      return;
    }

    if (this.isGeneratingVideo) {
      console.log('Video generation already in progress');
      return;
    }

    this.isGeneratingVideo = true;
    this.videoGenerationProgress = 0;
    this.videoGenerationStatus = 'Initializing...';
    this.videoGenerationCancelled = false;
    console.log('Starting video creation with', this._images.length, 'images');

    // Pre-load all images first
    this.videoGenerationStatus = 'Loading images...';
    console.log('Pre-loading all images...');
    let loadedImageData: Array<{image: HTMLImageElement, detail: SmartImageDetail}>;
    try {
      loadedImageData = await this.preloadAllImagesWithDetails(this._images);
      console.log(`All images processed: ${loadedImageData.length}/${this._images.length} loaded successfully`);
      this.videoGenerationProgress = 5;
      this.videoGenerationStatus = `Loaded ${loadedImageData.length} images`;
    } catch (error) {
      console.error('Failed to load images:', error);
      alert('Failed to load images for video generation. Please try again.');
      this.isGeneratingVideo = false;
      this.videoGenerationProgress = 0;
      return;
    }

    // Create a canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Calculate dimensions based on aspect ratio
    const [widthRatio, heightRatio] = this.aspectRatio.split(':').map(Number);
    const width = 1280; // HD width
    canvas.width = width;
    canvas.height = Math.round(width * (heightRatio / widthRatio));

    // Create video stream from canvas
    this.videoGenerationStatus = 'Preparing video encoder...';
    this.videoGenerationProgress = 10;
    
    const stream = canvas.captureStream(25); // 25 FPS
    const mimeType = 'video/webm;codecs=vp9';
    
    // Check if the MIME type is supported
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      console.error('MIME type not supported:', mimeType);
      alert('Video recording is not supported in this browser. Please try Chrome or Firefox.');
      this.isGeneratingVideo = false;
      this.videoGenerationProgress = 0;
      return;
    }
    
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: mimeType,
      videoBitsPerSecond: 20000000 // 10 Mbps - High quality
    });

    const chunks: Blob[] = [];
    
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/mp4' });
      this.downloadBlob(blob, 'timelapse.mp4');
      console.log('Video created successfully');
      this.videoGenerationStatus = 'Complete!';
      this.videoGenerationProgress = 100;
      setTimeout(() => {
        this.isGeneratingVideo = false;
        this.videoGenerationProgress = 0;
      }, 1000);
    };

    mediaRecorder.start();
    this.videoGenerationStatus = 'Recording video...';

    try {
      // Draw each pre-loaded image to the canvas with dynamic timing
      // 1 hour of real time = 1 second of video (3600:1 ratio)
      const HOURS_PER_SECOND = 3;
      const MILLISECONDS_PER_HOUR = 3600000;
      const TIME_SCALE = MILLISECONDS_PER_HOUR * HOURS_PER_SECOND; // Real time ms per video second
      const MIN_FRAME_DURATION = 100; // Minimum 100ms per frame
      const MAX_FRAME_DURATION = 5000; // Maximum 5s per frame
      const FPS = 25; // Frames per second
      
      for (let i = 0; i < loadedImageData.length; i++) {
        // Check if cancelled
        console.log(`Processing frame ${i + 1}/${loadedImageData.length} for ${loadedImageData[i].detail.timeStamp}...`);
        if (this.videoGenerationCancelled) {
          console.log('Video generation cancelled by user');
          mediaRecorder.stop();
          stream.getTracks().forEach(track => track.stop());
          this.isGeneratingVideo = false;
          this.videoGenerationProgress = 0;
          this.videoGenerationStatus = '';
          return;
        }
        
        const { image, detail } = loadedImageData[i];
        
        // Draw the image to canvas
        this.drawLoadedImageToCanvas(ctx, image, canvas.width, canvas.height);
        
        // Calculate frame duration based on time difference to next image
        let frameDuration = 1000; // Default 1 second
        if (i < loadedImageData.length - 1) {
          const currentTime = new Date(detail.timeStamp).getTime();
          const nextTime = new Date(loadedImageData[i + 1].detail.timeStamp).getTime();
          const realTimeDiff = nextTime - currentTime;
          
          // Scale time difference to video time
          frameDuration = (realTimeDiff / TIME_SCALE) * 1000;
          
          // Clamp to reasonable bounds
          frameDuration = Math.max(MIN_FRAME_DURATION, Math.min(MAX_FRAME_DURATION, frameDuration));
          
          console.log(`Frame ${i + 1}: ${Math.round(realTimeDiff / 60000)}min gap = ${Math.round(frameDuration)}ms`);
        }
        
        // Hold the frame for the calculated duration
        await this.delay(frameDuration);
        
        // Update progress
        const progress = 10 + Math.round((i + 1) / loadedImageData.length * 85);
        this.videoGenerationProgress = progress;
        this.videoGenerationStatus = `Recording frame ${i + 1} of ${loadedImageData.length}`;
        console.log(`✓ Recorded frame ${i + 1}/${loadedImageData.length} for ${frameDuration}ms`);
      }

      // Stop recording
      console.log('Finalizing video...');
      this.videoGenerationStatus = 'Finalizing video...';
      this.videoGenerationProgress = 95;
      mediaRecorder.stop();
      stream.getTracks().forEach(track => track.stop());
    } catch (error) {
      console.error('Error creating video:', error);
      mediaRecorder.stop();
      stream.getTracks().forEach(track => track.stop());
      this.isGeneratingVideo = false;
      this.videoGenerationProgress = 0;
      this.videoGenerationStatus = '';
    }
  }

  private async preloadAllImagesWithDetails(imageDetails: SmartImageDetail[]): Promise<Array<{image: HTMLImageElement, detail: SmartImageDetail}>> {
    console.log(`Starting to load ${imageDetails.length} images...`);
    const loadedData: Array<{image: HTMLImageElement, detail: SmartImageDetail}> = [];
    let failedCount = 0;

    // Load all images sequentially to ensure each completes before moving to the next
    for (let index = 0; index < imageDetails.length; index++) {
      const detail = imageDetails[index];
      
      try {
        const result = await this.loadSingleImage(detail, index, imageDetails.length);
        if (result) {
          loadedData.push(result);
          console.log(`✓ Successfully loaded image ${index + 1}/${imageDetails.length}: ${detail.timeStamp}`);
        } else {
          failedCount++;
          console.warn(`✗ Failed to load image ${index + 1}/${imageDetails.length}`);
        }
      } catch (error) {
        failedCount++;
        console.error(`✗ Error loading image ${index + 1}/${imageDetails.length}:`, error);
      }
    }
    
    if (failedCount > 0) {
      console.warn(`⚠️ ${failedCount} image(s) failed to load and will be skipped`);
    }
    
    if (loadedData.length === 0) {
      throw new Error('All images failed to load');
    }
    
    console.log(`✓ Successfully loaded ${loadedData.length}/${imageDetails.length} images`);
    return loadedData;
  }

  private loadSingleImage(detail: SmartImageDetail, index: number, total: number): Promise<{image: HTMLImageElement, detail: SmartImageDetail} | null> {
    return new Promise((resolve) => {
      const img = new Image();
      
      // Set crossOrigin BEFORE setting src - this is critical for CORS
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        resolve({ image: img, detail });
      };
      
      img.onerror = (error) => {
        console.error(`   URL: ${detail.src}`);
        console.error('   This is likely a CORS issue. Check Azure Blob Storage CORS settings.');
        resolve(null); // Resolve with null instead of rejecting, so we can continue
      };
      
      // Remove query parameters from URL before setting src
      const urlWithoutParams = detail.src.split('?')[0];
      img.src = urlWithoutParams;

      // Add a timeout in case the image hangs
      //console.log(img.src);
    });
  }

  private drawLoadedImageToCanvas(ctx: CanvasRenderingContext2D, img: HTMLImageElement, width: number, height: number): void {
    // Clear the canvas with black background
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);
    
    // Calculate scaling to fit image in canvas while maintaining aspect ratio
    const scale = Math.min(width / img.width, height / img.height);
    const x = (width - img.width * scale) / 2;
    const y = (height - img.height * scale) / 2;
    
    // Draw the image
    ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
    
    // Debug: Log that we drew an image
    console.log(`  → Drew image ${img.width}x${img.height} scaled to ${Math.round(img.width * scale)}x${Math.round(img.height * scale)}`);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
